import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { exportToCsv } from "@/lib/csv";

export default function HistoryPage() {
  const [list, setList] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [filter, setFilter] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [r, a] = await Promise.all([
          api.get("/reseller/recharges"),
          api.get("/reseller/allocations").catch(() => ({ data: [] })),
        ]);
        setList(r.data);
        setAllocations(a.data || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const inRange = (iso) => {
    if (!start && !end) return true;
    const d = new Date(iso);
    if (start && d < new Date(`${start}T00:00:00`)) return false;
    if (end && d > new Date(`${end}T23:59:59`)) return false;
    return true;
  };

  const filtered = useMemo(
    () => list.filter((r) => inRange(r.created_at) && (!filter || r.recipient_uid.includes(filter))),
    [list, filter, start, end]
  );

  const totals = useMemo(() => {
    const ranged = list.filter((r) => inRange(r.created_at));
    const success = ranged.filter((r) => r.status === "success");
    const diamondsSold = success.reduce((s, r) => s + (r.diamonds || 0), 0);
    const allocatedInRange = allocations
      .filter((a) => inRange(a.created_at))
      .reduce((s, a) => s + (a.amount || 0), 0);
    return { count: ranged.length, success: success.length, diamondsSold, allocatedInRange };
  }, [list, allocations, start, end]);

  return (
    <div className="space-y-6" data-testid="history-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Records</p>
        <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Recharge History</h1>
        <p className="text-zinc-500 mt-1">Track your recharges and allocations over any period.</p>
      </div>

      {/* Date filter + summary */}
      <div className="flex flex-wrap items-center gap-3" data-testid="history-date-filter">
        <span className="text-xs text-zinc-500">From</span>
        <input type="datetime-local" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)}
          data-testid="history-start-date" className="h-9 px-2 text-sm rounded-md border border-zinc-300 bg-white" />
        <span className="text-xs text-zinc-500">To</span>
        <input type="datetime-local" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)}
          data-testid="history-end-date" className="h-9 px-2 text-sm rounded-md border border-zinc-300 bg-white" />
        {(start || end) && (
          <button onClick={() => { setStart(""); setEnd(""); }} data-testid="history-clear-dates"
            className="px-2.5 py-1 rounded text-xs font-medium bg-red-50 text-red-700 hover:bg-red-100">Clear</button>
        )}
        <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by recipient UID"
          className="ml-auto w-full max-w-xs h-9 px-3 text-sm rounded-md border border-zinc-300 bg-white" data-testid="history-filter" />
        <button
          onClick={() => exportToCsv('reseller-recharges', filtered, [
            { header: 'Timestamp', accessor: r => new Date(r.created_at).toLocaleString() },
            { header: 'Platform', accessor: r => r.platform_name || r.platform },
            { header: 'Type', accessor: r => r.type === 'noble' ? `${r.tier_label || 'Noble'}${r.mode === 'renew' ? ' renew' : ''}` : 'Transfer' },
            { header: 'Recipient UID', accessor: r => r.recipient_uid },
            { header: 'Deducted', accessor: r => r.diamonds },
            { header: 'Delivered', accessor: r => r.transferred ?? r.diamonds },
            { header: 'Balance after', accessor: r => r.balance_after },
            { header: 'Status', accessor: r => r.status },
            { header: 'Note', accessor: r => r.note },
          ])}
          data-testid="history-export-csv"
          className="h-9 px-3 text-sm rounded-md border border-zinc-300 bg-white hover:bg-zinc-50 font-medium">Download CSV</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryTile label="Recharges (range)" value={totals.count.toLocaleString()} testId="history-total-count" />
        <SummaryTile label="Successful" value={totals.success.toLocaleString()} testId="history-success-count" />
        <SummaryTile label="Diamonds sold" value={totals.diamondsSold.toLocaleString()} testId="history-diamonds-sold" />
        <SummaryTile label="Allocated (range)" value={totals.allocatedInRange.toLocaleString()} testId="history-allocated-range" />
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              {["Timestamp", "Platform", "Type", "Recipient UID", "Deducted", "Delivered", "Balance after", "Status", "Note"].map((h) => (
                <th key={h} className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="9" className="px-6 py-12 text-center text-zinc-400 text-sm">{loading ? "Loading…" : "No recharges in this range"}</td></tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`history-row-${r.id}`}>
                <td className="px-6 py-3 text-sm text-zinc-700 font-mono">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-6 py-3 text-sm text-zinc-700">{r.platform_name || "Trumeet"}</td>
                <td className="px-6 py-3 text-sm">
                  {r.type === "noble" ? (
                    <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-amber-100 text-amber-800">
                      {r.tier_label || "Noble"}{r.mode === "renew" ? " · renew" : ""}
                    </span>
                  ) : (<span className="text-xs text-zinc-500">Transfer</span>)}
                </td>
                <td className="px-6 py-3 text-sm text-zinc-900 font-mono">{r.recipient_uid}</td>
                <td className="px-6 py-3 text-sm text-zinc-900 tabular-nums font-mono">{r.diamonds}</td>
                <td className="px-6 py-3 text-sm text-zinc-700 tabular-nums font-mono">{(r.transferred ?? r.diamonds)?.toLocaleString?.() ?? r.transferred ?? r.diamonds}</td>
                <td className="px-6 py-3 text-sm text-zinc-500 tabular-nums font-mono" data-testid={`history-balance-after-${r.id}`}>{r.balance_after != null ? r.balance_after.toLocaleString() : "—"}</td>
                <td className="px-6 py-3">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                    r.status === "success" ? "bg-green-100 text-green-800" :
                    r.status === "failed" ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
                  }`}>{r.status}</span>
                </td>
                <td className="px-6 py-3 text-sm text-zinc-600">{r.note || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, testId }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={testId}>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="font-display text-2xl font-semibold tabular-nums font-mono text-zinc-900 mt-1">{value}</div>
    </div>
  );
}
